import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import * as Analytics from 'analytics/analytics'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineExternalLinkAltIcon from 'components/Luxkit/Icons/line/LineExternalLinkAltIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import Group from 'components/utils/Group'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useIsMobileScreen } from 'hooks/useScreenSize'
import { useTripDocumentUrl } from 'tripPlanner/hooks/api/documents'

const StyledTextButtonWrapper = styled(Group)`
  height: 100%;
  position: relative;
`

const ContentContainer = styled(Group)`
  max-width: ${rem(500)};
`

const ButtonGroup = styled(Group)`
  &.align-bottom {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: ${rem(20)};
  }
`

interface Props {
  documentKey: string
}

function DocumentPdfView({ documentKey }: Props) {
  const { data: presignedUrl } = useTripDocumentUrl({ documentKey })

  const modalElement = useModalElementContext<boolean>()
  const closeModal = useCallback(() => {
    modalElement.resolve(false)
  }, [modalElement])

  const isMobile = useIsMobileScreen()

  useEffect(() => {
    Analytics.trackClientEvent({
      action: 'view',
      subject: 'trip_document',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    })
  }, [documentKey])

  const openedInNewTab = useRef(false)
  useEffect(() => {
    if (isMobile && presignedUrl && !openedInNewTab.current) {
      window.open(presignedUrl, '_blank')
      openedInNewTab.current = true
      closeModal()
    }
  }, [closeModal, isMobile, presignedUrl])

  return (
    <>
      {!presignedUrl && <LoadingIndicator />}
      {presignedUrl && (
        <object
          data={presignedUrl}
          width="100%"
          height="100%"
          aria-label="document"
        >
          <StyledTextButtonWrapper
            direction="vertical"
            verticalAlign="center"
            horizontalAlign="center"
          >
            <ContentContainer direction="vertical" gap={8}>
              <HeadingTextBlock variant="heading3" align="center">
                Unable to display preview
              </HeadingTextBlock>
              <BodyTextBlock variant="medium" align="center">
                We are unable to display your document. Please use the link
                below to open it in a new window
              </BodyTextBlock>
              <ButtonGroup
                direction={isMobile ? 'vertical-reverse' : 'horizontal'}
                horizontalAlign="center"
                gap={8}
                className={cn({ 'align-bottom': isMobile })}
              >
                <TextButton
                  kind="tertiary"
                  onClick={() => closeModal()}
                  fit={isMobile ? 'full-width' : undefined}
                  size={isMobile ? 'large' : undefined}
                >
                  Cancel
                </TextButton>
                <TextButton
                  kind="primary"
                  href={presignedUrl}
                  target="_blank"
                  fit={isMobile ? 'full-width' : undefined}
                  size={isMobile ? 'large' : undefined}
                  startIcon={<LineExternalLinkAltIcon />}
                >
                  View document
                </TextButton>
              </ButtonGroup>
            </ContentContainer>
          </StyledTextButtonWrapper>
        </object>
      )}
    </>
  )
}

export default DocumentPdfView
